export const FAMILY = "UNTRUSTED_DEVELOPMENT_STELLAR_PAYMENT_V3";
export const TESTNET_NETWORK_ID = "cee0302d59844d32bdca915c8203dd44b33fbb7edc19051ea37abedf28ecd472";
export const FIELD_MODULUS = 21888242871839275222246405745257275088548364400416034343698204186575808495617n;
export const MAX_AMOUNT = (1n << 63n) - 1n;
export const MAX_INPUTS = 32;
export const MAX_OUTPUTS = 5;
export const PAYLOAD_BYTES = 512;

export interface WalletDomain { networkId: string; poolId: string; assetId: string }
export interface CryptoAdapter {
  deriveMaster(input: { spendSecret: string; nullifyingKey: string }): Promise<string>;
  commitment(input: WalletDomain & { value: string; randomness: string; recipientMaster: string }): Promise<string>;
  nullifier(input: WalletDomain & { commitment: string; nullifyingKey: string }): Promise<string>;
}
export interface RecipientAddress extends WalletDomain {
  version: 3;
  masterPublicKey: string;
  encryptionPublicKey: string;
}
export interface WalletIdentity extends RecipientAddress {
  spendSecret: string;
  nullifyingKey: string;
  encryptionPrivateJwk: JsonWebKey;
  address: string;
}
export interface WalletNote {
  commitment: string;
  value: string;
  randomness: string;
  recipientMaster: string;
  nullifier: string;
  index: number;
  transactionHash?: string;
  statementHash: string;
  ledger: number;
  status: "unspent" | "reserved" | "spent";
  reservationId?: string;
  spentTransactionHash?: string;
  spentStatementHash?: string;
}
export type PendingKind = "deposit" | "send" | "withdraw" | "batch-withdraw" | "terminal-exit";
export interface SignedTransaction { txHash: string; signedXdr: string; source: string; statementHash?: string }
export interface PendingTransaction {
  id: string;
  intentDigest: string;
  kind: PendingKind;
  inputCommitments: string[];
  createdAt: number;
  status: "proving" | "signed" | "submitted";
  signed?: SignedTransaction;
}
export interface PublicOutput {
  index: number;
  commitment: string;
  payload: string;
  transactionHash?: string;
  statementHash: string;
  ledger: number;
  /** Position in the transaction's five output slots, independent of table index. */
  slot: number;
}
export interface ConfirmedReceipt extends WalletDomain {
  txHash: string;
  statementHash?: string;
  ledger: number;
  nullifiers: string[];
  outputs: PublicOutput[];
}
export interface SettledStatementReceipt extends WalletDomain {
  statementHash: string;
  ledger: number;
  nullifiers: string[];
  outputs: PublicOutput[];
}
export interface FailedReceipt { txHash: string; ledger: number }
export interface ExpiryEvidence { statementHash: string; observedLedger: number }
export type ReceiptRecord = { id: string; intentDigest: string } & (
  { outcome: "confirmed" | "failed"; txHash: string; statementHash?: string; ledger: number; observedLedger?: undefined }
  | { outcome: "settled"; statementHash: string; txHash?: undefined; ledger: number; observedLedger?: undefined }
  | { outcome: "expired"; statementHash: string; txHash?: undefined; submittedTxHash: string; ledger?: undefined; observedLedger: number }
);
export interface PrivatePayment { address: string; value: string }
export interface PaymentBatch {
  id: string;
  intentDigest: string;
  recipients: PrivatePayment[];
  steps: Array<{ id: string; recipientIndices: number[]; status: "ready" | "pending" | "confirmed" | "failed" | "expired"; txHash?: string; statementHash?: string }>;
}
export interface WalletData {
  version: 3;
  family: typeof FAMILY;
  domain: WalletDomain;
  identity: WalletIdentity;
  notes: WalletNote[];
  pending: PendingTransaction[];
  receipts: ReceiptRecord[];
  batches: PaymentBatch[];
  cursor: number;
}
export interface WalletSummary {
  address: string;
  availableStroops: string;
  reservedStroops: string;
  noteCount: number;
  cursor: number;
  pending: Array<Pick<PendingTransaction, "id" | "kind" | "status" | "createdAt"> & { txHash?: string }>;
}
export class WalletError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "WalletError";
  }
}
